import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import {
	APP_STORE_URL,
	PLAY_STORE_PACKAGE,
	PLAY_STORE_URL,
} from '../../../utils/mobileAppConstants';
import { isAndroid, isIOS, isIOSSafari, isMobileBrowser } from '../../../utils/userAgent';

// Banner is shown only on these two pre-login screens.
const BANNER_ROUTES = ['/signin-new', '/signin-new/password'];

const MobileAppBanner: React.FC = () => {
	const { pathname } = useLocation();
	const [dismissed, setDismissed] = useState(false);

	// Hide on irrelevant routes.
	if (!BANNER_ROUTES.includes(pathname)) return null;

	// Hide on desktop/tablet — banner is mobile-only.
	if (!isMobileBrowser()) return null;

	// Hide on iOS Safari — Apple's native smart-app-banner (the apple-itunes-app meta tag in index.html)
	// renders its own banner. Showing a second one stacked is bad UX.
	if (isIOSSafari()) return null;

	// User dismissed via × — stay hidden for the rest of the email→password flow.
	if (dismissed) return null;

	const storeUrl = isIOS() ? APP_STORE_URL : PLAY_STORE_URL;

	const handleCTA = (): void => {
		if (isAndroid()) {
			// Chrome on Android understands intent:// URLs natively. The browser tries to open
			// the app (via assetlinks.json + the app's intent-filter); if that fails, it falls
			// back to S.browser_fallback_url automatically. No JS timer dance needed.
			const host = window.location.host;
			const intentUrl =
				`intent://${host}/signin-new#Intent;` +
				`scheme=https;` +
				`package=${PLAY_STORE_PACKAGE};` +
				`S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};` +
				`end`;
			window.location.href = intentUrl;
			return;
		}

		// iOS-non-Safari (Chrome/Firefox on iOS): same-origin universal links don't fire,
		// and we have no custom URL scheme. Send straight to App Store.
		window.location.href = storeUrl;
	};

	const handleDismiss = (): void => {
		setDismissed(true);
	};

	return (
		<div
			style={{
				position: 'sticky',
				top: 0,
				zIndex: 1000,
				width: '100%',
				background: '#FFFFFF',
				borderBottom: '1px solid #E4E7EC',
				boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
				padding: '12px 16px',
				display: 'flex',
				alignItems: 'center',
				gap: '12px',
				fontFamily: 'inherit',
			}}
		>
			<button
				type='button'
				onClick={handleDismiss}
				aria-label='Dismiss banner'
				style={{
					background: 'transparent',
					border: 'none',
					padding: 4,
					cursor: 'pointer',
					color: '#667085',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexShrink: 0,
				}}
			>
				<CloseOutlined style={{ fontSize: 14 }} />
			</button>

			<img
				src='/logo.svg'
				alt='Veytan'
				style={{ width: 36, height: 36, flexShrink: 0 }}
			/>

			<div style={{ flex: 1, minWidth: 0 }}>
				<div
					style={{
						fontSize: 14,
						fontWeight: 600,
						color: '#101828',
						lineHeight: '20px',
					}}
				>
					Veytan
				</div>
				<div
					style={{
						fontSize: 12,
						color: '#667085',
						lineHeight: '18px',
					}}
				>
					Get a faster experience with the app
				</div>
			</div>

			<button
				type='button'
				onClick={handleCTA}
				style={{
					background: '#7F56D9',
					color: '#FFFFFF',
					border: 'none',
					borderRadius: 8,
					padding: '8px 14px',
					fontSize: 13,
					fontWeight: 600,
					lineHeight: '18px',
					cursor: 'pointer',
					whiteSpace: 'nowrap',
					flexShrink: 0,
				}}
				onMouseEnter={(e) => {
					(e.currentTarget as HTMLButtonElement).style.background = '#6941C6';
				}}
				onMouseLeave={(e) => {
					(e.currentTarget as HTMLButtonElement).style.background = '#7F56D9';
				}}
			>
				{isAndroid() ? 'Open in app' : 'Get the app'}
			</button>
		</div>
	);
};

export default MobileAppBanner;
